import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useMotionPresets } from '../../theme/motion';

export default function PageHeader({
  title,
  subtitle,
  actions,
  overline,
  compact = false,
  variant = 'default',
}) {
  const motionPresets = useMotionPresets();
  const theme = useTheme();
  const isDashboard = variant === 'dashboard';
  const dark = theme.palette.mode === 'dark';

  const actionsSx = isDashboard
    ? {
        flexWrap: 'wrap',
        '& .MuiIconButton-root': {
          color: alpha('#ffffff', 0.9),
        },
        '& .MuiButton-root': {
          color: '#ffffff',
        },
        '& .MuiButton-contained': {
          backgroundColor: alpha('#ffffff', 0.14),
          border: `1px solid ${alpha('#ffffff', 0.22)}`,
          boxShadow: 'none',
          '&:hover': { backgroundColor: alpha('#ffffff', 0.18), boxShadow: 'none' },
        },
        '& .MuiButton-outlined': {
          borderColor: alpha('#ffffff', 0.28),
          '&:hover': {
            borderColor: alpha('#ffffff', 0.36),
            backgroundColor: alpha('#ffffff', 0.08),
          },
        },
        '& .MuiButton-text': {
          '&:hover': { backgroundColor: alpha('#ffffff', 0.08) },
        },
      }
    : { flexWrap: 'wrap' };

  const content = (
    <>
      <Box>
        {overline ? (
          <Typography
            variant="caption"
            color={isDashboard ? alpha('#ffffff', 0.78) : 'text.secondary'}
            sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
          >
            {overline}
          </Typography>
        ) : null}
        <Typography
          variant={isDashboard ? (compact ? 'h4' : 'h3') : 'h4'}
          sx={{
            mt: overline ? 0.45 : 0,
            color: isDashboard ? '#ffffff' : undefined,
            fontWeight: isDashboard ? 820 : 760,
            lineHeight: compact ? 1.12 : undefined,
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography
            variant="body2"
            color={isDashboard ? alpha('#ffffff', 0.78) : 'text.secondary'}
            sx={{ mt: 0.75, maxWidth: 820 }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      {actions ? (
        <Stack direction="row" spacing={1.2} sx={actionsSx}>
          {actions}
        </Stack>
      ) : null}
    </>
  );

  if (isDashboard) {
    return (
      <Box component={motion.div} {...motionPresets.fadeUp} sx={{ mb: compact ? 1.8 : 2.4 }}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: compact ? 2.5 : 3.5,
            px: compact ? { xs: 1.7, sm: 2.25 } : { xs: 1.9, sm: 2.65 },
            py: compact ? { xs: 1.55, sm: 1.85 } : { xs: 2, sm: 2.35 },
            border: '1px solid',
            borderColor: alpha('#ffffff', dark ? 0.16 : 0.22),
            background: dark
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.82)} 0%, ${alpha(
                  theme.palette.primary.main,
                  0.62
                )} 55%, ${alpha(theme.palette.secondary.main, 0.42)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.92)} 0%, ${alpha(
                  theme.palette.primary.main,
                  0.76
                )} 55%, ${alpha(theme.palette.secondary.main, 0.58)} 100%)`,
            boxShadow: theme.shadows[6],
            backdropFilter: 'blur(12px)',
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background:
                `radial-gradient(900px 240px at 20% 0%, ${alpha('#ffffff', 0.18)}, transparent 60%),` +
                `radial-gradient(700px 240px at 100% 10%, ${alpha('#ffffff', 0.12)}, transparent 65%)`,
              mixBlendMode: 'overlay',
            }}
          />

          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: compact ? 'center' : 'flex-end',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            {content}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component={motion.div}
      {...motionPresets.fadeUp}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: compact ? 'center' : 'flex-end',
        justifyContent: 'space-between',
        gap: 2,
        mb: compact ? 1.8 : 2.2,
      }}
    >
      {content}
    </Box>
  );
}

